import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

interface UnsavedChangesValue {
  /**
   * A section reports whether it is holding edits, under its own identity.
   *
   * Keyed by source rather than a single flag: a screen can have more than one
   * section reporting at once — Personal Information and Change Password sit on
   * the same page — and with one shared boolean whichever effect ran last won,
   * so a clean section would clear a dirty one's flag and the guard went quiet.
   */
  setDirtyFor: (source: string, dirty: boolean, revert?: () => void) => void;
  /**
   * Runs `action` unless some section has unsaved edits, in which case the
   * confirmation is raised first and `action` runs only if the user discards.
   */
  guard: (action: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesValue>({
  setDirtyFor: () => undefined,
  guard: action => action(),
});

/**
 * Each caller gets a `setDirty` bound to its own identity, so consumers keep the
 * simple `setDirty(boolean)` signature and cannot overwrite each other.
 */
export const useUnsavedChanges = () => {
  const { setDirtyFor, guard } = useContext(UnsavedChangesContext);
  const source = useId();
  return useMemo(
    () => ({
      /**
       * `revert` is what "Discard changes" actually runs. Without it the prompt
       * only cleared its own flag: the typed values stayed on screen, the
       * section reported dirty again on its next effect, and the dialog had
       * lied about discarding anything.
       */
      setDirty: (dirty: boolean, revert?: () => void) => setDirtyFor(source, dirty, revert),
      guard,
    }),
    [setDirtyFor, guard, source]
  );
};

/**
 * Keeps a screen's unapplied edits from disappearing without a word.
 *
 * The read/edit cards apply on demand, which means there is a real window where
 * a user has typed something and not pressed Apply. Anything that would leave
 * that window — opening another card, switching section, leaving for another
 * part of the app, closing the tab — has to say so first rather than silently
 * dropping the work.
 *
 * It sits above the sidebar because the sidebar is one of those exits: it
 * navigates from a button's `onClick`, so it has to ask the guard itself.
 */
export function UnsavedChangesProvider({
  children,
}: {
  children: (confirm: {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
  }) => ReactNode;
}) {
  const [isDirty, setIsDirty] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);
  const [isPrompting, setIsPrompting] = useState(false);
  /**
   * Which sections hold edits, and how to throw each one away. Dirty means any
   * of them is.
   */
  const dirtySources = useRef<Map<string, (() => void) | undefined>>(new Map());

  const setDirtyFor = useCallback((source: string, dirty: boolean, revert?: () => void) => {
    if (dirty) dirtySources.current.set(source, revert);
    else dirtySources.current.delete(source);
    setIsDirty(dirtySources.current.size > 0);
  }, []);

  const guard = useCallback(
    (action: () => void) => {
      if (!isDirty) {
        action();
        return;
      }
      pendingAction.current = action;
      setIsPrompting(true);
    },
    [isDirty]
  );

  // The browser's own prompt for the one exit React cannot intercept.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome needs a returnValue set; the text itself is ignored.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const onConfirm = () => {
    const action = pendingAction.current;
    pendingAction.current = null;
    setIsPrompting(false);
    // Actually discard, then clear. Each section drops its own edits, so the
    // screen matches what the dialog just promised.
    const reverts = Array.from(dirtySources.current.values());
    dirtySources.current.clear();
    setIsDirty(false);
    reverts.forEach(revert => revert?.());
    action?.();
  };

  const onCancel = () => {
    pendingAction.current = null;
    setIsPrompting(false);
  };

  return (
    <UnsavedChangesContext.Provider value={{ setDirtyFor, guard }}>
      {children({ isOpen: isPrompting, onConfirm, onCancel })}
    </UnsavedChangesContext.Provider>
  );
}
